import { ApolloError } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import { FormActions } from '@openx/components-core/lib/FormActions/FormActions.tsx';
import { FormContainer } from '@openx/components-core/lib/FormContainer/FormContainer.tsx';
import { FormErrors } from '@openx/components-core/lib/FormErrors/FormErrors.tsx';
import { scrollToError } from '@openx/components-core/utils/scrollToError.ts';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { parseApolloFieldErrors } from '../../graphql/parseApolloFieldErrors.ts';
import type { AdvertiserOption, ContentCategoryOption } from '../types.ts';
import { CreativeContentDetailsSection } from './CreativeContentDetailsSection.tsx';
import { CreativeContentSection } from './CreativeContentSection.tsx';
import { CreativeDetailsSection } from './CreativeDetailsSection.tsx';
import { CREATIVE_FIELD_PATH_MAP } from './constants.ts';
import type { CreativeFormState } from './validation/validationSchema.ts';
import { getCreativeValidationSchema } from './validation/validationSchema.ts';

type CreativeFormFieldsProps = {
  advertiserOptions: AdvertiserOption[];
  iabCategoryOptions: ContentCategoryOption[];
  isEdit?: boolean;
  initialValues: CreativeFormState;
  onSubmit: (data: CreativeFormState) => Promise<void>;
};

export const CreativeFormFields = ({
  advertiserOptions,
  iabCategoryOptions,
  isEdit = false,
  initialValues,
  onSubmit: onSubmitHandler,
}: CreativeFormFieldsProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const formMethods = useForm<CreativeFormState>({
    defaultValues: initialValues,
    mode: 'all',
    resolver: yupResolver(getCreativeValidationSchema(t)),
  });

  const { isDirty } = formMethods.formState;
  const { setError } = formMethods;
  const [formErrors, setFormErrors] = useState<string[]>([]);

  const onSubmit = async (data: CreativeFormState) => {
    setFormErrors([]);

    try {
      await onSubmitHandler(data);
    } catch (error) {
      const defaultErrorMessage = isEdit ? t('Failed to update creative') : t('Failed to create creative');

      if (error instanceof ApolloError) {
        const { fieldErrors, generalErrors } = parseApolloFieldErrors(error, CREATIVE_FIELD_PATH_MAP);

        fieldErrors.forEach(({ fieldId, message }) => {
          setError(fieldId, { message });
        });

        setFormErrors([...generalErrors, ...fieldErrors.map(({ message }) => message)]);
        enqueueSnackbar(error.graphQLErrors?.[0]?.message ?? defaultErrorMessage, { variant: 'error' });
      } else {
        enqueueSnackbar(defaultErrorMessage, { variant: 'error' });
      }
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(onSubmit, scrollToError)}>
        <FormErrors errors={formErrors} />
        <FormContainer fullPage>
          <Box>
            <CreativeDetailsSection advertiserOptions={advertiserOptions} isEdit={isEdit} />
            <CreativeContentSection />
            <CreativeContentDetailsSection iabCategoryOptions={iabCategoryOptions} />
          </Box>

          <FormActions
            submitButtonLabel={t('Save')}
            cancelButtonLabel={t('Cancel')}
            onCancel={() => navigate(-1)}
            isSubmitting={formMethods.formState.isSubmitting}
            disabled={(isEdit && !isDirty) || formMethods.formState.isSubmitting}
          />
        </FormContainer>
      </form>
    </FormProvider>
  );
};
